import type { EventInstance, TimeFormat } from '../types';
import { addDays, startOfDay, endOfDay, formatAgendaDate, formatDateKey } from '../utils';
import { AgendaItem } from './AgendaItem';

interface AgendaViewProps {
  currentDate: Date;
  events: EventInstance[];
  daysAhead: number;
  timeFormat?: TimeFormat;
  onClickEvent: (event: EventInstance) => void;
  agendaGrouping?: 'day' | 'calendar' | 'flat';
  dimPastEvents?: boolean;
  eventColorPriority?: 'event' | 'calendar';
}

interface EventGroup {
  key: string;
  label: string;
  events: EventInstance[];
}

export function AgendaView({
  currentDate,
  events,
  daysAhead,
  timeFormat,
  onClickEvent,
  agendaGrouping = 'day',
  dimPastEvents = false,
  eventColorPriority = 'event',
}: AgendaViewProps) {
  // Collect events within the date range
  const today = startOfDay(currentDate);
  const rangeEnd = endOfDay(addDays(today, daysAhead - 1));

  const rangeEvents = events.filter((e) => {
    const start = new Date(e.startAt);
    const end = new Date(e.endAt);
    return start < rangeEnd && end > today;
  });

  if (rangeEvents.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
        No events in the next {daysAhead} days
      </div>
    );
  }

  let groups: EventGroup[];

  if (agendaGrouping === 'flat') {
    // Sort chronologically, no grouping
    const sorted = [...rangeEvents].sort(
      (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
    );
    groups = [{ key: 'all', label: '', events: sorted }];
  } else if (agendaGrouping === 'calendar') {
    // Group by calendar
    const calMap = new Map<string, EventInstance[]>();
    for (const event of rangeEvents) {
      const calKey = event.calendarId;
      if (!calMap.has(calKey)) calMap.set(calKey, []);
      calMap.get(calKey)!.push(event);
    }
    groups = Array.from(calMap.entries()).map(([calId, calEvents]) => ({
      key: calId,
      label: calEvents[0]?.calendarName || calId,
      events: calEvents.sort(
        (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
      ),
    }));
  } else {
    // Group by day (default)
    groups = [];
    for (let i = 0; i < daysAhead; i++) {
      const date = addDays(today, i);
      const dayStart = startOfDay(date);
      const dayEnd = endOfDay(date);

      const dayEvents = rangeEvents.filter((e) => {
        const start = new Date(e.startAt);
        const end = new Date(e.endAt);
        return start < dayEnd && end > dayStart;
      });

      if (dayEvents.length > 0) {
        groups.push({
          key: formatDateKey(date),
          label: formatAgendaDate(date),
          events: dayEvents,
        });
      }
    }
  }

  return (
    <div className="flex-1 overflow-auto">
      {groups.map((group) => (
        <div key={group.key} className="mb-4">
          {/* Group header */}
          {group.label && (
            <div className="sticky top-0 bg-background/95 backdrop-blur-sm px-4 py-2 border-b">
              <span className="text-sm font-semibold">{group.label}</span>
            </div>
          )}

          {/* Events */}
          <div className="py-1">
            {group.events.map((event) => (
              <AgendaItem
                key={event.id}
                event={event}
                onClick={() => onClickEvent(event)}
                timeFormat={timeFormat}
                dimPastEvents={dimPastEvents}
                eventColorPriority={eventColorPriority}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
